import * as THREE from 'three';
import { StandardNodeLibrary } from 'three/webgpu';
import type { NodeMaterial } from 'three/webgpu';
import { Fn, instanceIndex, int, ivec2, materialOpacity, textureLoad } from 'three/tsl';
import { cloneOwnedMaterial } from '../../../material';

/**
 * Unit 独占的实例槽绘制遮罩。只抑制原不透明绘制，不修改矩阵、Geometry 或拾取身份。
 * 使用纹理存储避免 uniform 数组容量限制；状态只在命令变化时上传，不在渲染循环中重建。
 */
export class ImportedModelInstanceSlotMask {
  /** 表面和边线共用的非持久化遮罩纹理。 */
  public readonly texture: THREE.DataTexture;
  /** 每个槽的可见标志，纹理空余像素保持可见。 */
  private readonly pixels: Uint8Array;
  /** 从原配方创建的独占节点材质，不拥有配方纹理。 */
  private readonly materials = new Map<THREE.Material, NodeMaterial>();
  /** 被本遮罩替换的材质入口，退出时原样归还。 */
  private readonly originals = new Map<THREE.Mesh | THREE.LineSegments, THREE.Material | THREE.Material[]>();
  /** 官方材质适配保留标准、物理、基础材质等原配置。 */
  private readonly library = new StandardNodeLibrary();
  /** 当前遮罩宽度，实例编号按行映射到像素。 */
  private readonly width: number;

  /** @param count 正式 Unit 的固定槽位数量。 @param initiallyVisible 新遮罩默认是否绘制全部槽。 */
  public constructor(private readonly count: number, initiallyVisible = true) {
    this.width = Math.max(1, Math.ceil(Math.sqrt(count)));
    const height = Math.max(1, Math.ceil(count / this.width));
    this.pixels = new Uint8Array(this.width * height).fill(initiallyVisible ? 255 : 0);
    this.texture = new THREE.DataTexture(this.pixels, this.width, height, THREE.RedFormat);
    this.texture.needsUpdate = true;
  }

  /** @param index 正式槽位。 @param visible 是否保留原不透明绘制。 */
  public setVisible(index: number, visible: boolean): void {
    if (index < 0 || index >= this.count) throw new Error(`实例外观槽位越界：${index}`);
    const value = visible ? 255 : 0;
    if (this.pixels[index] === value) return;
    this.pixels[index] = value;
    this.texture.needsUpdate = true;
  }

  /**
   * 创建独占遮罩材质；不修改原材质的透明状态或原 alpha-test 规则。
   * @param source 正式渲染配方。
   * @returns 与本遮罩同生命周期的节点材质。
   */
  public resolveMaterial(source: THREE.Material): NodeMaterial {
    const cached = this.materials.get(source);
    if (cached) return cached;
    const owned = cloneOwnedMaterial(source);
    const material = this.library.fromMaterial(owned);
    if (!material || material.fragmentNode) {
      owned.dispose();
      throw new Error(`实例临时外观不支持该片元材质：${source.type}`);
    }
    const originalOpacity = material.opacityNode ?? materialOpacity;
    // instanceIndex 是 uint；纹素坐标需要 int，显式转换保持原整数寻址语义。
    const mask = textureLoad(this.texture, ivec2(
      int(instanceIndex.mod(this.width)), int(instanceIndex.div(this.width)),
    )).r;
    material.opacityNode = Fn(() => {
      // 在 alpha、光照及深度写入之前丢弃原槽，透明副本才不会叠在不透明底面上。
      mask.lessThan(0.5).discard();
      return originalOpacity;
    })();
    this.materials.set(source, material);
    if (material !== owned) owned.dispose();
    return material;
  }

  /** @param object 正式表面或默认边线；不改 source 材质。 */
  public bindObject(object: THREE.Mesh | THREE.LineSegments): void {
    if (this.originals.has(object)) return;
    const original = object.material;
    const masked = Array.isArray(original)
      ? original.map(material => this.resolveMaterial(material))
      : this.resolveMaterial(original);
    this.originals.set(object, original);
    object.material = masked;
  }

  /** @param object 即将被显示模式释放的边线，先归还其自有原材质。 */
  public unbindObject(object: THREE.Mesh | THREE.LineSegments): void {
    const original = this.originals.get(object);
    if (!original) return;
    object.material = original;
    this.originals.delete(object);
    const borrowed = new Set<THREE.Material>();
    this.originals.forEach(input => (Array.isArray(input) ? input : [input]).forEach(material => borrowed.add(material)));
    this.materials.forEach((material, source) => {
      if (!borrowed.has(source)) { material.dispose(); this.materials.delete(source); }
    });
  }

  /** 释放所有本层创建的渲染绑定和遮罩纹理，不销毁借用的业务配方。 */
  public dispose(): void {
    this.originals.forEach((material, object) => { object.material = material; });
    this.originals.clear();
    this.materials.forEach(material => material.dispose());
    this.materials.clear();
    this.texture.dispose();
  }
}
